/**
 * Control imports or they will cause cyclic-requires
 * Process variables that are not part of secrets
 */
import * as _ from 'lodash';
import { z } from 'zod/v4';
import { type BuildTag } from '@epihub/core/src/epikit/types';
import { Logger } from '@epihub/core/src/logger';

export type NodeEnv =
  | 'production'
  | 'development'
  | 'local';

export type LogFormat =
  | 'json'
  | 'pretty';

export const zodProjectVarsEnv = z.enum([
  // Only 1 production should ever exist
  'production',
  // Branch previews and any other environment
  'staging',
  // Local to the machine
  'local',
]);

export type ProjectVarsEnv = z.infer<typeof zodProjectVarsEnv>;

export type TProcessEnv = {
  NODE_ENV: NodeEnv;

  /**
   * Used by @epihub/core/src/logger to switch the output format,
   * remote servers should use 'json', local dev and tests should use 'pretty'
   * Default is 'pretty'
   */
  LOG_FORMAT?: LogFormat;

  PACKAGE_VARS: string;
  PACKAGE_VARS_STALE?: string;

  /**
   * Should not be used internally but it reflects the current state of the code
   */
  BUILD_TAG: BuildTag;
  BRANCH: string;
  ENVIRONMENT: ProjectVarsEnv;
};

class ProcessBase {
  readonly env: TProcessEnv = (() => {
    return {
      get NODE_ENV() {
        return (process.env.NODE_ENV ?? 'development') as NodeEnv;
      },
      get LOG_FORMAT() {
        return (process.env.LOG_FORMAT ?? 'json') as LogFormat;
      },
      get PACKAGE_VARS() {
        return process.env.PACKAGE_VARS ?? '{}';
      },
      get PACKAGE_VARS_STALE() {
        return process.env.PACKAGE_VARS_STALE ?? 'false';
      },
      get BUILD_TAG() {
        return (process.env.BUILD_TAG ?? 'x-x:v0.0.0~') as BuildTag;
      },
      get BRANCH() {
        return process.env.BRANCH ?? '';
      },
      get ENVIRONMENT() {
        return (process.env.ENVIRONMENT ?? '') as ProjectVarsEnv;
      },
    };
  })();

  readonly updateEnv = (newEnv: TProcessEnv & { [other: string]: string }) => {
    _.keys(newEnv).forEach((key) => {
      if (key === 'PACKAGE_VARS') {
        process.env.PACKAGE_VARS_STALE = `${new Date().toISOString()}`;
      }

      process.env[key] = newEnv[key];
    });
    Logger.restartMainLogger();
  };

  readonly exit = (code = 0) => {
    process.exit(code);
  };
}

export const Process = new ProcessBase();
